import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsExporting from "highcharts/modules/exporting";

import { IconClock, IconHistory } from "@tabler/icons-react";
import SystemStatusTable from "../elements/SystemStatusTable";
import { IconWorld } from "@tabler/icons-react";

// enable exporting if available
if (typeof Highcharts === "object") {
    HighchartsExporting(Highcharts);
}

/* ========= i18n ========= */
const dict = {
    en: {
        // charts
        cpuUsage: "CPU Usage",
        selectRange: "Select range to zoom in",
        usagePct: "Usage (%)",
        max100: "Maximum 100%",
        usage: "Usage",

        ramUsage: "RAM Usage",
        usedMemoryGB: "Used Memory (GB)",
        maxGB: (v) => `Maximum ${v}GB`,
        usedMemory: "Used memory",

        hddUsageFor: (part) => `HDD Usage for ${part}`,
        btn1H: "1H",
        btn1D: "1D",
        btn1W: "1W",
        btn1M: "1M",
        btnAll: "All",

        // status blocks
        cpuStatus: "CPU Status",
        ramStatus: "RAM Status",
        hddStatus: "HDD Status",

        // SystemStatusTable rows
        lastUpdate: "Last Update",
        upTime: "Up Time",
        ethernetTraffic: "Ethernet Traffic",

        // Recharts labels
        cpuLabel: "CPU",
        ramLabel: "RAM",
    },

    ru: {
        cpuUsage: "Загрузка CPU",
        selectRange: "Выберите диапазон для увеличения",
        usagePct: "Использование (%)",
        max100: "Максимум 100%",
        usage: "Использование",

        ramUsage: "Использование RAM",
        usedMemoryGB: "Используемая память (GB)",
        maxGB: (v) => `Максимум ${v}GB`,
        usedMemory: "Используемая память",

        hddUsageFor: (part) => `Использование HDD для ${part}`,
        btn1H: "1Ч",
        btn1D: "1Д",
        btn1W: "1Н",
        btn1M: "1М",
        btnAll: "Все",

        cpuStatus: "Статус CPU",
        ramStatus: "Статус RAM",
        hddStatus: "Статус HDD",

        lastUpdate: "Последнее обновление",
        upTime: "Время работы",
        ethernetTraffic: "Ethernet-трафик",

        cpuLabel: "CPU",
        ramLabel: "RAM",
    },

    ua: {
        cpuUsage: "Завантаження CPU",
        selectRange: "Виберіть діапазон для збільшення",
        usagePct: "Використання (%)",
        max100: "Максимум 100%",
        usage: "Використання",

        ramUsage: "Використання RAM",
        usedMemoryGB: "Використана пам'ять (GB)",
        maxGB: (v) => `Максимум ${v}GB`,
        usedMemory: "Використана пам'ять",

        hddUsageFor: (part) => `Використання HDD для ${part}`,
        btn1H: "1Г",
        btn1D: "1Д",
        btn1W: "1Т",
        btn1M: "1М",
        btnAll: "Усі",

        cpuStatus: "Статус CPU",
        ramStatus: "Статус RAM",
        hddStatus: "Статус HDD",

        lastUpdate: "Останнє оновлення",
        upTime: "Час роботи",
        ethernetTraffic: "Ethernet-трафік",

        cpuLabel: "CPU",
        ramLabel: "RAM",
    },
};

const getInitialLang = () =>
    (localStorage.getItem("lang") || "en").toLowerCase();

const ChartPlaceholder = ({ label }) => (
    <div
        className="border rounded-4 bg-light-subtle shadow-sm text-center d-flex flex-column align-items-center justify-content-center gap-2 px-4"
        style={{ minHeight: 260, marginBottom: 16 }}
    >
        <div className="fw-bold fs-5 text-dark">{label}</div>
        <div className="text-muted small">No data yet</div>
    </div>
);

/* ========= charts (receive t) ========= */
const CpuChart = ({ t, cpu }) => {
    const val = parseFloat(cpu);
    if (Number.isNaN(val)) return <ChartPlaceholder label={t.cpuUsage} />;
    return (
        <HighchartsReact
            highcharts={Highcharts}
            options={{
                chart: { type: "column" },
                title: { text: t.cpuUsage },
                xAxis: { categories: [t.cpuLabel] },
                yAxis: { max: 100, title: { text: t.usagePct } },
                series: [{ name: t.cpuLabel, data: [val], color: "#4f46e5" }],
            }}
        />
    );
};

const RamChart = ({ t, ramUsed, ramAll }) => {
    const used = parseFloat(ramUsed);
    const total = parseFloat(ramAll);
    if (Number.isNaN(used) || Number.isNaN(total) || total <= 0) return <ChartPlaceholder label={t.ramUsage} />;
    const percent = Math.min(100, (used / total) * 100);
    return (
        <HighchartsReact
            highcharts={Highcharts}
            options={{
                chart: { type: "column" },
                title: { text: t.ramUsage },
                xAxis: { categories: [t.ramLabel] },
                yAxis: { max: 100, title: { text: t.usagePct } },
                series: [{ name: t.ramLabel, data: [percent], color: "#10b981" }],
            }}
        />
    );
};

const HddChart = ({ t, hddJson, hdd, hddAll }) => {
    let dataArr = [];
    if (hddJson) {
        try {
            const parsed = JSON.parse(hddJson);
            if (Array.isArray(parsed)) {
                dataArr = parsed
                    .map((p) => {
                        const val = parseFloat(p.value ?? p.used ?? p.usage ?? p.percent);
                        const name = p.name || p.part || p.mount || "HDD";
                        return Number.isNaN(val) ? null : { name, value: val };
                    })
                    .filter(Boolean);
            }
        } catch (e) { /* ignore */ }
    }
    if (!dataArr.length && hddAll && hdd !== undefined && hdd !== null) {
        const percent = Math.min(100, (parseFloat(hdd) / parseFloat(hddAll)) * 100);
        if (!Number.isNaN(percent)) dataArr = [{ name: "HDD", value: percent }];
    }
    if (!dataArr.length) return <ChartPlaceholder label={t.hddStatus} />;

    const options = {
        chart: { type: "column" },
        title: { text: t.hddStatus },
        xAxis: { categories: dataArr.map((d) => d.name) },
        yAxis: { max: 100, title: { text: t.usagePct } },
        series: [{ name: t.hddStatus, data: dataArr.map((d) => d.value), color: "#f59e0b" }],
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

/* ========= system status (receive t) ========= */
const SystemStatus = ({ t, details }) => {
    const cpuVal = parseFloat(details?.cpu);
    const cpuData = Number.isNaN(cpuVal) ? [] : [{ name: t.cpuLabel, value: cpuVal }];

    const ramUsed = parseFloat(details?.ram);
    const ramAll = parseFloat(details?.ram_all);
    const ramPercent =
        !Number.isNaN(ramUsed) && !Number.isNaN(ramAll) && ramAll > 0
            ? Math.min(100, (ramUsed / ramAll) * 100)
            : null;
    const ramData = ramPercent === null ? [] : [{ name: t.ramLabel, value: ramPercent }];

    let hddData = [];
    if (details?.hdd_json) {
        try {
            const parsed = JSON.parse(details.hdd_json);
            if (Array.isArray(parsed)) {
                hddData = parsed
                    .map((p) => {
                        const val = parseFloat(p.value ?? p.used ?? p.usage ?? p.percent);
                        const name = p.name || p.part || p.mount || "HDD";
                        return Number.isNaN(val) ? null : { name, value: val };
                    })
                    .filter(Boolean);
            }
        } catch (e) {
            hddData = [];
        }
    }
    if (!hddData.length && details?.hdd_all && details?.hdd !== undefined && details?.hdd !== null) {
        const percent = Math.min(100, (parseFloat(details.hdd) / parseFloat(details.hdd_all)) * 100);
        if (!Number.isNaN(percent)) hddData = [{ name: "HDD", value: percent }];
    }

    return (
        <div className="d-flex flex-column flex-lg-row gap-4 justify-content-center">
            <div className="card shadow-sm rounded-4 w-100">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="card-title mb-0">{t.cpuStatus}</h5>
                        <span className="badge bg-primary-subtle text-primary">Live</span>
                    </div>
                    <BarChart
                        width={350}
                        height={220}
                        data={cpuData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} tickFormatter={(tick) => `${tick}%`} />
                        <YAxis type="category" dataKey="name" />
                        <Tooltip />
                        <Bar dataKey="value" fill="#4f46e5" radius={6} />
                    </BarChart>
                    {!cpuData.length && <div className="text-muted small">No data</div>}
                </div>
            </div>

            <div className="card shadow-sm rounded-4 w-100">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="card-title mb-0">{t.ramStatus}</h5>
                        <span className="badge bg-success-subtle text-success">RAM</span>
                    </div>
                    <BarChart
                        width={350}
                        height={220}
                        data={ramData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 64]} tickFormatter={(tick) => `${tick}GB`} />
                        <YAxis type="category" dataKey="name" />
                        <Tooltip />
                        <Bar dataKey="value" fill="#10b981" radius={6} />
                    </BarChart>
                    {!ramData.length && <div className="text-muted small">No data</div>}
                </div>
            </div>

            <div className="card shadow-sm rounded-4 w-100">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="card-title mb-0">{t.hddStatus}</h5>
                        <span className="badge bg-warning-subtle text-warning">HDD</span>
                    </div>
                    <BarChart
                        width={350}
                        height={220}
                        data={hddData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} tickFormatter={(tick) => `${tick}%`} />
                        <YAxis type="category" dataKey="name" />
                        <Tooltip />
                        <Bar dataKey="value" fill="#f59e0b" radius={6} />
                    </BarChart>
                    {!hddData.length && <div className="text-muted small">No data</div>}
                </div>
            </div>
        </div>
    );
};

/* ========= main component ========= */
const Dashboard = ({ lang: langProp, singleArgs }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const details = singleArgs?.details || {};

    const systemItems = [
        { name: t.lastUpdate, value: details.last_update || "—", icon: <IconClock size={30} color="var(--bs-info)" /> },
        { name: t.upTime, value: details.uptime || "—", icon: <IconHistory size={30} color="var(--bs-info)" /> },
        { name: t.ethernetTraffic, value: details.ip_address || details.host || "—", icon: <IconHistory size={30} color="var(--bs-info)" /> },
    ];

    const heroStyle = {
        background:
            "linear-gradient(120deg, #f3f4f6 0%, #e0f2fe 45%, #dbeafe 100%), radial-gradient(circle at 18% 15%, rgba(255,255,255,0.85), transparent 35%), radial-gradient(circle at 82% 22%, rgba(255,255,255,0.55), transparent 35%)",
        color: "#0f172a",
        boxShadow: "0 12px 32px rgba(59,130,246,0.12)",
        border: "1px solid rgba(59,130,246,0.12)",
    };

    return (
        <>
            <div className="rounded-4 p-4 shadow-sm mb-4" style={heroStyle}>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                        <div className="text-uppercase fw-semibold small text-muted">{t.cpuStatus}</div>
                        <h2 className="mb-2" style={{ textShadow: "0 3px 10px rgba(15,23,42,0.15)" }}>
                            {details.name || details.host || "Server"}
                        </h2>
                        {details.host && (
                            <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-white text-dark fw-semibold shadow-sm">
                                <IconWorld size={18} />
                                <span>{details.host}</span>
                            </div>
                        )}
                    </div>
                    <div className="text-end">
                        {/* оставляем правую часть пустой, чтобы не показывать Last Update / Up Time в hero */}
                    </div>
                </div>
            </div>

            <SystemStatusTable systemItems={systemItems} softwareItems={[]} />
            <SystemStatus t={t} details={details} />
            <CpuChart t={t} cpu={details.cpu} />
            <RamChart t={t} ramUsed={details.ram} ramAll={details.ram_all} />
            <HddChart t={t} hddJson={details.hdd_json} hdd={details.hdd} hddAll={details.hdd_all} />
        </>
    );
};

export default Dashboard;
